import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import googleAdsIconDark from "../assets/icons/google-ads-dark.svg";
import googleAdsIconLight from "../assets/icons/google-ads-light.svg";
import ThinScroll from "./shared/ThinScroll";

/**
 * Legacy (flat icon-only) navigation sidebar.
 *
 * Default state for all projects unless `new_menu` feature is enabled.
 * 80px wide, vertical icon stack with small text labels underneath.
 *
 * Each item is:
 *  - Active (highlighted blue) when `activePage` matches
 *  - Disabled (dashed border, grayed) when feature not enabled for current project
 *  - Clickable (normal hover) otherwise
 *
 * Accepts both legacy keys (e.g. "event-intelligence") and new short keys (e.g. "events")
 * so pages can use the same activePage prop regardless of which sidebar renders.
 */
export default function NavSidebarLegacy({ activePage }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const view = useView();

  const role = view.role;
  const isClient = view.isClient;
  const dashboardPath = isClient ? "/client-dashboard" : "/dashboard";

  // Normalize activePage so both old and new key formats work
  const normalizeKey = (k) => {
    if (k === "events") return "event-intelligence";
    if (k === "route") return "route-intelligence";
    if (k === "competitive") return "competitive-intelligence";
    return k;
  };
  const active = normalizeKey(activePage);

  const {
    showChat,
    showVoice,
    showBookings,
    showGoogleAds,
    showGoogleAnalytics,
    showCdp,
    showEventIntelligence,
    showAim,
    showRouteIntelligence,
    showCompetitiveIntelligence,
  } = view;

  const ITEMS = [
    {
      key: "home",
      label: "Home",
      enabled: true,
      route: dashboardPath,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-current" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      dividerAfter: true,
    },
    {
      key: "chat",
      label: "Chat",
      enabled: showChat,
      route: dashboardPath,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-current" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
        </svg>
      ),
    },
    {
      key: "voice",
      label: "Voice",
      enabled: showVoice,
      route: dashboardPath,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-current" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0" />
          <path d="M12 19v4" />
        </svg>
      ),
      dividerAfter: true,
    },
    {
      key: "booking",
      label: "Booking",
      enabled: showBookings,
      route: "/bookings",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" className="text-current" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
    },
    {
      key: "google-ads",
      label: "Google Ads",
      enabled: showGoogleAds,
      route: "/google-ads",
      icon: (
        <img
          src={isDarkMode ? googleAdsIconLight : googleAdsIconDark}
          alt="Google Ads"
          width={24}
          height={24}
          className={!showGoogleAds && active !== "google-ads" ? "opacity-40 grayscale" : ""}
        />
      ),
    },
    {
      key: "analytics",
      label: "Website Analytics",
      enabled: showGoogleAnalytics,
      route: "/google-analytics",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="5" height="17" rx="2.5" fill="currentColor" />
          <rect x="10" y="9" width="5" height="12" rx="2.5" fill="currentColor" />
          <rect x="17" y="14" width="5" height="7" rx="2.5" fill="currentColor" />
        </svg>
      ),
      dividerAfter: true,
    },
    {
      key: "visitors",
      label: "Visitors",
      enabled: showCdp,
      route: "/visitors",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      key: "event-intelligence",
      label: "Events",
      enabled: showEventIntelligence,
      route: "/event-intelligence",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
          <path d="M12 13l1.2 2.6 2.8.4-2 2 .5 2.8L12 19.5 9.5 20.8l.5-2.8-2-2 2.8-.4z" />
        </svg>
      ),
    },
    {
      key: "aim",
      label: "AI Visibility",
      enabled: showAim,
      route: "/ai-visibility",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-current">
          <path d="M13 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L5.5 10l5.6-1.9L13 2.5z" />
          <path d="M19 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z" />
        </svg>
      ),
    },
    {
      key: "route-intelligence",
      label: "Route Intel",
      enabled: showRouteIntelligence,
      route: "/route-intelligence",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current">
          <circle cx="5" cy="6" r="2" />
          <circle cx="19" cy="18" r="2" />
          <path d="M5 8c0 7 14 1 14 8" />
        </svg>
      ),
    },
    {
      key: "competitive-intelligence",
      label: "Competitive Intel",
      enabled: showCompetitiveIntelligence,
      route: "/competitive-intelligence",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current">
          <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 012 0v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <ThinScroll
        as="aside"
        className="hidden md:flex flex-col w-[88px] border-r dark:border-gray-800 bg-white dark:bg-gray-900 fixed left-0 overflow-y-auto z-30"
        style={{
          top: 'var(--app-header-h, 72px)',
          height: 'calc(100vh - var(--app-header-h, 72px))',
        }}
      >
      <div className="px-2 pt-4 pb-2"></div>
      <nav className="flex-1 px-2 pb-4 space-y-2">
        {ITEMS.map((item) => {
          const isActive = active === item.key;
          const isDisabled = !item.enabled && !isActive;

          return (
            <React.Fragment key={item.key}>
              <button
                type="button"
                disabled={isActive}
                onClick={() => {
                  if (!isDisabled) navigate(item.route);
                }}
                className={`w-full flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg border transition-all ${
                  isActive
                    ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                    : isDisabled
                    ? "border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-pointer"
                }`}
                title={isDisabled ? `${item.label} - Not Available` : item.label}
              >
                {item.icon}
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
              {item.dividerAfter && <div className="border-t dark:border-gray-700 my-2"></div>}
            </React.Fragment>
          );
        })}
      </nav>
    </ThinScroll>
    {/* Reserves layout space for the fixed sidebar so the flex row still aligns. */}
    <div className="hidden md:block w-[88px] shrink-0" aria-hidden="true" />
    </>
  );
}
